import React, { useEffect, useState } from 'react';
import { learnerApi } from '../../api/client';
import { useAuth } from '../../context/AuthContext';
import Spinner from '../../components/common/Spinner';
import { FaUser, FaEnvelope, FaPhone, FaDoorOpen } from 'react-icons/fa';

const DAYS = ['Monday','Tuesday','Wednesday','Thursday','Friday'];
const COLORS = ['#0d6efd','#10b981','#f59e0b','#8b5cf6','#ef4444','#06b6d4','#84cc16','#ec4899'];

export default function LearnerTimetable() {
  const { school } = useAuth();
  const [slots,     setSlots]     = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [selected,  setSelected]  = useState(null); // clicked slot for detail
  const [view,      setView]      = useState('week'); // 'week' | 'list'

  const primary = school?.primary_color || '#0d6efd';

  useEffect(() => {
    learnerApi.getTimetable()
      .then(r => setSlots(r.data?.data || []))
      .finally(() => setLoading(false));
  }, []);

  const subjects = [...new Set(slots.map(s => s.subject_name))];
  const subjectColor = name => COLORS[subjects.indexOf(name) % COLORS.length];
  const today = new Date().toLocaleString('en', { weekday: 'long' });

  const byDay = DAYS.reduce((acc, d) => ({
    ...acc, [d]: slots.filter(s => s.day_of_week === d).sort((a, b) => a.start_time.localeCompare(b.start_time))
  }), {});

  if (loading) return <Spinner />;
  if (slots.length === 0) return (
    <div>
      <div className="page-header"><h2>My Timetable</h2></div>
      <div className="sms-card" style={{ textAlign: 'center', color: '#9ca3af', padding: '60px 0' }}>
        No timetable published for your grade yet
      </div>
    </div>
  );

  return (
    <div>
      <div className="page-header" style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div><h2>My Timetable</h2><p>{subjects.length} subjects · {slots.length} periods per week</p></div>
        <div style={{ display: 'flex', gap: 4, background: '#f3f4f6', borderRadius: 10, padding: 4 }}>
          {[['week','Week View'],['list','List View']].map(([k,l]) => (
            <button key={k} onClick={() => setView(k)} className="sms-btn"
              style={{ background: view===k?'#fff':'transparent', boxShadow: view===k?'0 1px 4px rgba(0,0,0,0.1)':'none',
                color: view===k?'#111':'#6b7280', padding:'6px 14px', borderRadius:8, fontSize:'0.82rem' }}>{l}</button>
          ))}
        </div>
      </div>

      {/* Subject colour legend */}
      <div style={{ display:'flex', gap:8, flexWrap:'wrap', marginBottom:20 }}>
        {subjects.map(s => (
          <div key={s} style={{ display:'flex', alignItems:'center', gap:5, background:`${subjectColor(s)}11`,
            border:`1px solid ${subjectColor(s)}33`, borderRadius:20, padding:'4px 12px', fontSize:'0.78rem', fontWeight:600, color:subjectColor(s) }}>
            <div style={{ width:8, height:8, borderRadius:'50%', background:subjectColor(s) }} />
            {s}
          </div>
        ))}
      </div>

      {/* Week grid view */}
      {view === 'week' && (
        <div style={{ display:'grid', gridTemplateColumns:'repeat(5,1fr)', gap:12 }}>
          {DAYS.map(day => (
            <div key={day}>
              <div style={{ textAlign:'center', fontWeight:700, fontSize:'0.85rem', padding:'10px 8px',
                borderRadius:10, marginBottom:8,
                background: day === today ? primary : '#f3f4f6',
                color: day === today ? '#fff' : '#374151' }}>
                {day}
                {day === today && <div style={{ fontSize:'0.62rem', opacity:0.85, marginTop:1 }}>Today</div>}
              </div>
              <div style={{ display:'flex', flexDirection:'column', gap:8 }}>
                {byDay[day].map((s, i) => {
                  const col = subjectColor(s.subject_name);
                  return (
                    <button key={i} onClick={() => setSelected(selected?.id === s.id ? null : s)}
                      style={{ padding:'10px 12px', borderRadius:10, border:`2px solid ${selected?.id === s.id ? col : col + '44'}`,
                        background: selected?.id === s.id ? `${col}18` : `${col}09`,
                        cursor:'pointer', textAlign:'left', transition:'all 0.15s', width:'100%' }}>
                      <div style={{ fontWeight:700, fontSize:'0.82rem', color:col }}>{s.subject_name}</div>
                      <div style={{ fontSize:'0.72rem', color:'#6b7280', marginTop:2 }}>
                        {s.start_time.slice(0,5)} – {s.end_time.slice(0,5)}
                      </div>
                      <div style={{ fontSize:'0.7rem', color:'#9ca3af', marginTop:1 }}>
                        {s.teacher_first_name} {s.teacher_surname}
                      </div>
                      {s.room && (
                        <div style={{ fontSize:'0.68rem', color:'#9ca3af', display:'flex', alignItems:'center', gap:3, marginTop:1 }}>
                          <FaDoorOpen size={9} /> {s.room}
                        </div>
                      )}
                    </button>
                  );
                })}
                {byDay[day].length === 0 && (
                  <div style={{ textAlign:'center', color:'#e5e7eb', fontSize:'0.78rem', padding:'20px 0' }}>Free</div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* List view */}
      {view === 'list' && (
        <div className="sms-card" style={{ padding:0, overflow:'hidden' }}>
          <table className="sms-table">
            <thead>
              <tr><th>Day</th><th>Time</th><th>Subject</th><th>Teacher</th><th>Room</th></tr>
            </thead>
            <tbody>
              {DAYS.flatMap(day => byDay[day].map((s,i) => {
                const col = subjectColor(s.subject_name);
                return (
                  <tr key={`${day}-${i}`} onClick={() => setSelected(selected?.id === s.id ? null : s)}
                    style={{ cursor:'pointer', background: selected?.id === s.id ? `${col}09` : 'transparent' }}>
                    <td style={{ fontWeight:600, color: day===today ? primary : '#374151' }}>
                      {i===0 ? day : ''}
                      {i===0 && day===today && <span style={{ marginLeft:6, fontSize:'0.7rem', background:primary, color:'#fff', padding:'1px 6px', borderRadius:10 }}>Today</span>}
                    </td>
                    <td style={{ fontSize:'0.83rem', color:'#6b7280', whiteSpace:'nowrap' }}>
                      {s.start_time.slice(0,5)} – {s.end_time.slice(0,5)}
                    </td>
                    <td>
                      <span style={{ display:'inline-flex', alignItems:'center', gap:6 }}>
                        <span style={{ width:8, height:8, borderRadius:'50%', background:col, flexShrink:0 }} />
                        <strong style={{ color:col }}>{s.subject_name}</strong>
                        {s.subject_code && <span style={{ color:'#9ca3af', fontSize:'0.75rem' }}>({s.subject_code})</span>}
                      </span>
                    </td>
                    <td style={{ fontSize:'0.83rem' }}>
                      {s.teacher_first_name} {s.teacher_surname}
                    </td>
                    <td style={{ fontSize:'0.82rem', color:'#6b7280' }}>{s.room || '—'}</td>
                  </tr>
                );
              }))}
            </tbody>
          </table>
        </div>
      )}

      {/* Slot detail panel */}
      {selected && (
        <div style={{ marginTop:20, padding:20, borderRadius:14, border:`2px solid ${subjectColor(selected.subject_name)}44`,
          background:`${subjectColor(selected.subject_name)}06` }}>
          <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:16 }}>
            <div>
              <h4 style={{ fontWeight:800, color:subjectColor(selected.subject_name), margin:0 }}>{selected.subject_name}</h4>
              <div style={{ color:'#6b7280', fontSize:'0.82rem', marginTop:3 }}>
                {selected.day_of_week} · {selected.start_time.slice(0,5)} – {selected.end_time.slice(0,5)}
                {selected.room && <span> · Room {selected.room}</span>}
              </div>
            </div>
            <button onClick={() => setSelected(null)}
              style={{ background:'none', border:'none', cursor:'pointer', color:'#9ca3af', fontSize:'1.1rem', lineHeight:1 }}>×</button>
          </div>

          {selected.teacher_id ? (
            <div style={{ display:'flex', alignItems:'center', gap:14 }}>
              <div style={{ width:52, height:52, borderRadius:'50%', background:`${subjectColor(selected.subject_name)}22`,
                border:`2px solid ${subjectColor(selected.subject_name)}55`,
                display:'flex', alignItems:'center', justifyContent:'center', overflow:'hidden', flexShrink:0 }}>
                {selected.teacher_avatar
                  ? <img src={selected.teacher_avatar} alt="" style={{ width:'100%', height:'100%', objectFit:'cover' }} />
                  : <span style={{ fontWeight:800, color:subjectColor(selected.subject_name) }}>
                      {selected.teacher_first_name?.[0]}{selected.teacher_surname?.[0]}
                    </span>
                }
              </div>
              <div>
                <div style={{ fontWeight:700 }}>{selected.teacher_first_name} {selected.teacher_surname}</div>
                {selected.teacher_qualification && <div style={{ fontSize:'0.78rem', color:'#6b7280' }}>{selected.teacher_qualification}</div>}
                <div style={{ display:'flex', gap:14, marginTop:5 }}>
                  {selected.teacher_email && (
                    <a href={`mailto:${selected.teacher_email}`}
                      style={{ display:'flex', alignItems:'center', gap:5, color:subjectColor(selected.subject_name), fontSize:'0.8rem', textDecoration:'none' }}>
                      <FaEnvelope size={11} /> {selected.teacher_email}
                    </a>
                  )}
                  {selected.teacher_phone && (
                    <a href={`tel:${selected.teacher_phone}`}
                      style={{ display:'flex', alignItems:'center', gap:5, color:subjectColor(selected.subject_name), fontSize:'0.8rem', textDecoration:'none' }}>
                      <FaPhone size={11} /> {selected.teacher_phone}
                    </a>
                  )}
                </div>
              </div>
            </div>
          ) : <div style={{ color:'#9ca3af', fontSize:'0.83rem' }}>No teacher assigned</div>}
        </div>
      )}
    </div>
  );
}
